var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// (c) 2023 Acellera Ltd http://www.acellera.com
// All Rights Reserved
// No redistribution in whole or part
//
import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Fade, DialogActions, CircularProgress, DialogContentText, Grid, Link, FormHelperText, TextField, DialogContent, } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import { FormProvider, useForm } from "react-hook-form";
import { AutocompleteSelectPM } from "../SharedFormParts/AutocompleteSelect";
import { TextFieldPM } from "../SharedFormParts/TextField";
import { defaultValues, RegistrationSchema, } from "./RegistrationSchema";
import countryList from "../static/assets/countryList.json";
import { userAuthStore } from "./userAuthState";
import { DCBrowserStore } from "../DatacenterBrowser";
import { shallow } from "zustand/shallow";
import { executeWithTokenRefresh } from ".";
import { dispatchConfirmationDialogEvent } from "../utils";
export function RegistrationForm({ onSuccess, onLoginClick, }) {
    const [showingForm, setShowingForm] = useState(true);
    const [showConfirmMail, setShowConfirmMail] = useState(false);
    const [userAuthData, setUserAuthData] = useState(undefined);
    const handleRegistrationSuccess = (userData) => {
        setUserAuthData(userData);
        setTimeout(() => setShowingForm(false), 500);
        setTimeout(() => setShowConfirmMail(true), 700);
    };
    const handleConfirmationSuccess = () => __awaiter(this, void 0, void 0, function* () {
        setShowConfirmMail(false);
        yield dispatchConfirmationDialogEvent({
            message: "Registration was successful!",
            noCancelButton: true,
        });
        onSuccess();
    });
    return (_jsxs(_Fragment, { children: [_jsx(Fade, Object.assign({ in: showingForm, unmountOnExit: true }, { children: _jsx("div", { children: _jsx(UserDataForm, { onSuccess: handleRegistrationSuccess, onLoginClick: onLoginClick }) }) })), _jsx(Fade, Object.assign({ in: showConfirmMail, unmountOnExit: true }, { children: _jsx("div", { children: _jsx(MailConfirmation, { onSuccess: handleConfirmationSuccess, userAuthData: userAuthData }) }) }))] }));
}
function UserDataForm({ onSuccess, onLoginClick, }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const formMethods = useForm({
        resolver: yupResolver(RegistrationSchema),
        defaultValues: defaultValues,
    });
    const { handleSubmit, register, formState: { errors }, } = formMethods;
    const onSubmit = (data) => __awaiter(this, void 0, void 0, function* () {
        setIsSubmitting(true);
        setErrorMessage("");
        const response = yield executeWithTokenRefresh(() => fetch(`${window.location.origin}/v2/auth/signup`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        }), true);
        if (response.success) {
            setTimeout(() => setIsSubmitting(false), 200);
            onSuccess(data);
        }
        else {
            setIsSubmitting(false);
            const default_error = "Service temporarily unavailable. Please try again later.";
            if (!response.error || response.error === "unknown error") {
                setErrorMessage(default_error);
            }
            else {
                setErrorMessage(response.error);
            }
        }
    });
    return (_jsx("form", Object.assign({ onSubmit: handleSubmit(onSubmit) }, { children: _jsx(FormProvider, Object.assign({}, formMethods, { children: _jsxs(Grid, Object.assign({ container: true, direction: "column", spacing: 2 }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(TextFieldPM, { formKey: "username", label: "Name", error: (_a = errors["username"]) === null || _a === void 0 ? void 0 : _a.message }) })), _jsx(Grid, Object.assign({ xs: true, item: true }, { children: _jsx(TextFieldPM, { formKey: "mail", label: "Email", error: (_b = errors["mail"]) === null || _b === void 0 ? void 0 : _b.message }) })), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(TextFieldPM, { type: "password", formKey: "password", label: "Password", error: (_c = errors["password"]) === null || _c === void 0 ? void 0 : _c.message }) })), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(TextFieldPM, { type: "password", formKey: "passwordConfirm", label: "Confirm password", error: (_d = errors["passwordConfirm"]) === null || _d === void 0 ? void 0 : _d.message }) })), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(TextFieldPM, { formKey: "affiliation", label: "Affiliation", error: (_e = errors["affiliation"]) === null || _e === void 0 ? void 0 : _e.message }) })), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(AutocompleteSelectPM, { hookFormRegisterFunc: register, formKey: "country", label: "Country", error: (_f = errors["country"]) === null || _f === void 0 ? void 0 : _f.message, options: countryList }) })), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(TextFieldPM, { formKey: "city", label: "City", error: (_g = errors["city"]) === null || _g === void 0 ? void 0 : _g.message }) })), !!errorMessage && (_jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(DialogContentText, Object.assign({ component: FormHelperText, textAlign: "center", error: true }, { children: errorMessage })) }))), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(DialogActions, Object.assign({ sx: { justifyContent: "center" } }, { children: _jsx(Button, Object.assign({ type: "submit", variant: "contained", disabled: isSubmitting, endIcon: isSubmitting ? (_jsx(CircularProgress, { size: 20 })) : (_jsx(HowToRegIcon, {})) }, { children: "Sign Up" })) })) })), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(DialogContentText, Object.assign({ textAlign: "center" }, { children: _jsx(Link, Object.assign({ component: "button", underline: "hover", variant: "body1", onClick: onLoginClick }, { children: "I already have an account." })) })) }))] })) })) })));
}
function MailConfirmation({ onSuccess, userAuthData, }) {
    const [code, setCode] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [project] = DCBrowserStore((state) => [state.project], shallow);
    const { login, onSuccessfulLogin } = userAuthStore((state) => ({
        login: state.login,
        onSuccessfulLogin: state.onSuccessfulLogin,
    }));
    const onSubmitConfirmation = () => __awaiter(this, void 0, void 0, function* () {
        setIsSubmitting(true);
        setErrorMessage("");
        const response = yield executeWithTokenRefresh(() => fetch(`${window.location.origin}/v2/auth/verify/mail`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                mail: userAuthData === null || userAuthData === void 0 ? void 0 : userAuthData.mail,
                code: code,
            }),
        }), true);
        if (!response.success) {
            setIsSubmitting(false);
            const default_error = "Service temporarily unavailable. Please try again later.";
            if (!response.error || response.error === "unknown error") {
                setErrorMessage(default_error);
            }
            else {
                setErrorMessage(response.error);
            }
            return;
        }
        //login
        if (userAuthData) {
            const loginData = yield login(userAuthData.mail, userAuthData.password);
            if (loginData.success && loginData.data) {
                onSuccessfulLogin(project);
            }
        }
        setTimeout(() => setIsSubmitting(false), 200);
        onSuccess();
    });
    return (_jsxs(_Fragment, { children: [_jsx(DialogContentText, Object.assign({ textAlign: "center" }, { children: "Please enter the confirmation code we sent to your email to complete your registration." })), _jsx(DialogContent, Object.assign({ sx: {
                    maxWidth: "20rem",
                    margin: "auto",
                } }, { children: _jsx(TextField, { fullWidth: true, size: "small", id: "smiles", margin: "dense", variant: "outlined", label: "Confirmation code", value: code, onChange: (e) => setCode(e.target.value) }) })), _jsxs(Grid, Object.assign({ container: true, direction: "column", spacing: 2 }, { children: [!!errorMessage && (_jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(DialogContentText, Object.assign({ component: FormHelperText, textAlign: "center", error: true }, { children: errorMessage })) }))), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(DialogActions, Object.assign({ sx: { justifyContent: "center" } }, { children: _jsx(Button, Object.assign({ variant: "contained", onClick: onSubmitConfirmation, disabled: isSubmitting, endIcon: isSubmitting ? (_jsx(CircularProgress, { size: 20 })) : (_jsx(CheckIcon, { color: "secondary" })) }, { children: "Confirm" })) })) }))] }))] }));
}
function PasswordStrengthHint() {
    return (_jsx(Fade, Object.assign({ in: true, timeout: 1000 }, { children: _jsx(DialogContentText, Object.assign({ color: "secondary", variant: "caption", textAlign: "left" }, { children: "User password MUST have at least 8 characters and contain at least: a lower-case letter, an upper-case letter, a number and a symbol/special character." })) })));
}
